import { Sheet } from '@src/utils/download-utils';
import { DrawerData } from '../components/ChannelDrawer/channel-drawer-data';
import { CPTBRow, DashboardData } from '../dtc-commerce-mmm-types';
import {
  efficiencyOf,
  marketDisplayName,
  marketDmaCode,
  MarketSummary,
  MarketTotals
} from './dtc-commerce-mmm-markets';

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * <prefix>_<client>_[<channel>_]<visual>, where the prefix names the surface
 * (dtc-mmm / commerce-mmm). Excel/PNG suffix is added by the downstream util.
 * ChannelSlug is only used for drawer downloads.
 *
 * Prefer the bound `buildExportFilename` off the dashboard context, which supplies
 * the prefix for the current variant.
 */
export const buildExportFilename = (
  prefix: string,
  client: string,
  visual: string,
  channelSlug?: string
): string => {
  const parts = [prefix, slugify(client), channelSlug ? slugify(channelSlug) : null, visual];
  return parts.filter((p): p is string => Boolean(p)).join('_');
};

// Used to blank CI cells when the upstream CSV provided nothing for an
// (attribution_type, feature) pair. We can't distinguish "true zero" from
// "missing" at the row level, but if every row aggregates to exactly 0/0
// for a feature the safest read is "no CI in source".
const hasCi = (ciLo: number, ciHi: number): boolean => ciLo !== 0 || ciHi !== 0;

const isRevenueModel = (data: DashboardData): boolean => data.modelInfo.kpiType === 'revenue';

const kpiLabelOf = (data: DashboardData): string =>
  isRevenueModel(data) ? 'Revenue' : 'Conversions';

// Only one is set, by the model's KPI.
interface EfficiencyColumn {
  ROAS?: number | '';
  CPA?: number | '';
}

const efficiencyColumn = (
  spend: number,
  contribution: number,
  isRevenue: boolean
): EfficiencyColumn => {
  const value = efficiencyOf(spend, contribution, isRevenue) ?? '';
  return isRevenue ? { ROAS: value } : { CPA: value };
};

interface AttributionAggregate {
  spend: number;
  contribution: number;
  ciLo: number;
  ciHi: number;
  rowCount: number;
}

const emptyAggregate = (): AttributionAggregate => ({
  spend: 0,
  contribution: 0,
  ciLo: 0,
  ciHi: 0,
  rowCount: 0
});

const aggregateRows = (rows: CPTBRow[]): AttributionAggregate => {
  const agg = emptyAggregate();
  rows.forEach(r => {
    agg.spend += r.rawSpend;
    agg.contribution += r.effectiveNormalized;
    agg.ciLo += r.ciLo;
    agg.ciHi += r.ciHi;
    agg.rowCount += 1;
  });
  return agg;
};

interface FeatureMeta {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brandNonbrand: string;
  mediaType: string;
}

const featureMetaOf = (row: CPTBRow): FeatureMeta => ({
  name: row.name,
  channel: row.channel,
  platform: row.platform,
  tactic: row.tactic,
  brandNonbrand: row.brandNonbrand,
  mediaType: row.mediaType
});

// Group a cptb array by feature name, capturing a sample row for metadata.
const groupByFeature = (rows: CPTBRow[]): Map<string, { meta: FeatureMeta; rows: CPTBRow[] }> => {
  const byName = new Map<string, { meta: FeatureMeta; rows: CPTBRow[] }>();
  rows.forEach(r => {
    const existing = byName.get(r.name);
    if (existing) {
      existing.rows.push(r);
    } else {
      byName.set(r.name, { meta: featureMetaOf(r), rows: [r] });
    }
  });
  return byName;
};

interface FeatureExportRow extends EfficiencyColumn {
  Channel: string;
  Platform: string;
  Tactic: string;
  'Brand/Nonbrand': string;
  'Media Type': string;
  'Attribution Type': 'Direct' | 'Actualized';
  Spend: number;
  Contribution: number;
  'Spend Share %': number;
  'Contribution Share %': number;
  'CI Lo': number | '';
  'CI Hi': number | '';
}

const buildFeatureRow = (
  meta: FeatureMeta,
  agg: AttributionAggregate,
  totals: { spend: number; contribution: number },
  attributionType: 'Direct' | 'Actualized',
  isRevenue: boolean
): FeatureExportRow => ({
  Channel: meta.channel,
  Platform: meta.platform,
  Tactic: meta.tactic,
  'Brand/Nonbrand': meta.brandNonbrand,
  'Media Type': meta.mediaType,
  'Attribution Type': attributionType,
  Spend: agg.spend,
  Contribution: agg.contribution,
  'Spend Share %': totals.spend > 0 ? (agg.spend / totals.spend) * 100 : 0,
  'Contribution Share %':
    totals.contribution > 0 ? (agg.contribution / totals.contribution) * 100 : 0,
  ...efficiencyColumn(agg.spend, agg.contribution, isRevenue),
  'CI Lo': hasCi(agg.ciLo, agg.ciHi) ? agg.ciLo : '',
  'CI Hi': hasCi(agg.ciLo, agg.ciHi) ? agg.ciHi : ''
});

/**
 * Shared shape for Bar and Table views. Two rows per feature (Direct +
 * Actualized) aggregated over the date window. Most-granular always — Group
 * By and the in-container channel filter are ignored at this layer.
 */
export const shapeBarTableExport = (data: DashboardData): Sheet[] => {
  const isRevenue = isRevenueModel(data);
  const directByName = groupByFeature(data.raw.cptbT12M);
  const actualizedByName = groupByFeature(data.actualized.cptbT12M);
  const allNames = new Set<string>([...directByName.keys(), ...actualizedByName.keys()]);

  const directTotals = { spend: 0, contribution: 0 };
  const actualizedTotals = { spend: 0, contribution: 0 };
  directByName.forEach(({ rows }) => {
    const agg = aggregateRows(rows);
    directTotals.spend += agg.spend;
    directTotals.contribution += agg.contribution;
  });
  actualizedByName.forEach(({ rows }) => {
    const agg = aggregateRows(rows);
    actualizedTotals.spend += agg.spend;
    actualizedTotals.contribution += agg.contribution;
  });

  const exportRows: FeatureExportRow[] = [];
  const sortedNames = Array.from(allNames).sort();
  sortedNames.forEach(name => {
    const direct = directByName.get(name);
    const actualized = actualizedByName.get(name);
    const meta = direct?.meta ?? actualized?.meta;
    if (!meta) return;
    if (direct) {
      exportRows.push(
        buildFeatureRow(meta, aggregateRows(direct.rows), directTotals, 'Direct', isRevenue)
      );
    }
    if (actualized) {
      exportRows.push(
        buildFeatureRow(
          meta,
          aggregateRows(actualized.rows),
          actualizedTotals,
          'Actualized',
          isRevenue
        )
      );
    }
  });

  return [{ name: 'Features', data: exportRows }];
};

interface LineExportRow extends EfficiencyColumn {
  Date: string;
  Channel: string;
  Platform: string;
  Tactic: string;
  'Brand/Nonbrand': string;
  'Media Type': string;
  'Attribution Type': 'Direct' | 'Actualized';
  Spend: number;
  Contribution: number;
}

const buildLineRowsFor = (
  rows: CPTBRow[],
  attributionType: 'Direct' | 'Actualized',
  isRevenue: boolean
): LineExportRow[] =>
  rows.map(r => ({
    Date: r.date,
    Channel: r.channel,
    Platform: r.platform,
    Tactic: r.tactic,
    'Brand/Nonbrand': r.brandNonbrand,
    'Media Type': r.mediaType,
    'Attribution Type': attributionType,
    Spend: r.rawSpend,
    Contribution: r.effectiveNormalized,
    ...efficiencyColumn(r.rawSpend, r.effectiveNormalized, isRevenue)
  }));

/**
 * Line view export — long format, one row per (feature, attribution_type,
 * date). Honors the date window but stays at the source's native daily/weekly
 * granularity; the user can re-aggregate offline.
 */
export const shapeLineExport = (data: DashboardData): Sheet[] => {
  const isRevenue = isRevenueModel(data);
  const direct = buildLineRowsFor(data.raw.cptbT12M, 'Direct', isRevenue);
  const actualized = buildLineRowsFor(data.actualized.cptbT12M, 'Actualized', isRevenue);
  const combined = [...direct, ...actualized].sort((a, b) => {
    if (a.Date !== b.Date) return a.Date.localeCompare(b.Date);
    if (a.Channel !== b.Channel) return a.Channel.localeCompare(b.Channel);
    return a['Attribution Type'].localeCompare(b['Attribution Type']);
  });
  return [{ name: 'Spend & Contribution Over Time', data: combined }];
};

/**
 * Attribution Breakdown export — three sheets. Baseline is always included
 * as a Source='Baseline / Organic' row regardless of UI state. Reads from
 * actualized cptb (the model output) since that's what the visual shows.
 */
export const shapeAttributionBreakdownExport = (data: DashboardData): Sheet[] => {
  const kpiLabel = kpiLabelOf(data);
  const shareOfTotalKey = `% of Total ${kpiLabel}`;
  const totalActualKpi = data.actualVsPredictedT12M.reduce((s, r) => s + r.actualKpi, 0);
  const totalBaseline = data.actualVsPredictedT12M.reduce((s, r) => s + r.baselinePrediction, 0);
  const totalPaidMedia = data.actualized.cptbT12M.reduce((s, r) => s + r.effectiveNormalized, 0);

  // --- Summary sheet
  const featureGroups = groupByFeature(data.actualized.cptbT12M);
  const summaryRows: Record<string, string | number>[] = [];
  summaryRows.push({
    Source: 'Baseline / Organic',
    Channel: '',
    Platform: '',
    Tactic: '',
    'Brand/Nonbrand': '',
    Contribution: totalBaseline,
    [shareOfTotalKey]: totalActualKpi > 0 ? (totalBaseline / totalActualKpi) * 100 : 0,
    '% of Paid Media': ''
  });
  Array.from(featureGroups.values())
    .map(({ meta, rows }) => ({ meta, agg: aggregateRows(rows) }))
    .sort((a, b) => b.agg.contribution - a.agg.contribution)
    .forEach(({ meta, agg }) => {
      summaryRows.push({
        Source: 'Paid Media Feature',
        Channel: meta.channel,
        Platform: meta.platform,
        Tactic: meta.tactic,
        'Brand/Nonbrand': meta.brandNonbrand,
        Contribution: agg.contribution,
        [shareOfTotalKey]: totalActualKpi > 0 ? (agg.contribution / totalActualKpi) * 100 : 0,
        '% of Paid Media': totalPaidMedia > 0 ? (agg.contribution / totalPaidMedia) * 100 : 0
      });
    });
  summaryRows.push({
    Source: 'Total',
    Channel: '',
    Platform: '',
    Tactic: '',
    'Brand/Nonbrand': '',
    Contribution: totalBaseline + totalPaidMedia,
    [shareOfTotalKey]:
      totalActualKpi > 0 ? ((totalBaseline + totalPaidMedia) / totalActualKpi) * 100 : 0,
    '% of Paid Media': totalPaidMedia > 0 ? 100 : ''
  });

  // --- Over Time sheet
  const overTimeRows: Record<string, string | number>[] = [];
  data.actualVsPredictedT12M.forEach(r => {
    overTimeRows.push({
      Date: r.date,
      Source: 'Baseline / Organic',
      Channel: '',
      Platform: '',
      Tactic: '',
      'Brand/Nonbrand': '',
      Contribution: r.baselinePrediction
    });
  });
  data.actualized.cptbT12M.forEach(r => {
    overTimeRows.push({
      Date: r.date,
      Source: 'Paid Media Feature',
      Channel: r.channel,
      Platform: r.platform,
      Tactic: r.tactic,
      'Brand/Nonbrand': r.brandNonbrand,
      Contribution: r.effectiveNormalized
    });
  });
  overTimeRows.sort((a, b) => {
    const dateA = String(a.Date);
    const dateB = String(b.Date);
    return dateA.localeCompare(dateB);
  });

  // --- Period Totals sheet
  const paidMediaByDate = new Map<string, number>();
  data.actualized.cptbT12M.forEach(r => {
    paidMediaByDate.set(r.date, (paidMediaByDate.get(r.date) ?? 0) + r.effectiveNormalized);
  });
  const periodTotals = data.actualVsPredictedT12M
    .map(r => {
      const paidMedia = paidMediaByDate.get(r.date) ?? 0;
      return {
        Date: r.date,
        Baseline: r.baselinePrediction,
        'Paid Media Total': paidMedia,
        'Predicted Total': r.baselinePrediction + paidMedia,
        [`Actual ${kpiLabel}`]: r.actualKpi
      };
    })
    .sort((a, b) => a.Date.localeCompare(b.Date));

  return [
    { name: 'Decomposition Summary', data: summaryRows },
    { name: 'Decomposition Over Time', data: overTimeRows },
    { name: 'Period Totals', data: periodTotals }
  ];
};

/**
 * Channel Rank export — one row per (date bucket, source). Baseline is always
 * ranked alongside features. Date buckets follow the source date granularity
 * since the user can re-aggregate downstream.
 */
export const shapeRankExport = (data: DashboardData): Sheet[] => {
  const contributionByDate = new Map<
    string,
    { key: string; row: Record<string, string | number> }[]
  >();

  data.actualized.cptbT12M.forEach(r => {
    const list = contributionByDate.get(r.date) ?? [];
    list.push({
      key: `feature:${r.name}`,
      row: {
        Date: r.date,
        Source: 'Paid Media Feature',
        Channel: r.channel,
        Platform: r.platform,
        Tactic: r.tactic,
        'Brand/Nonbrand': r.brandNonbrand,
        Contribution: r.effectiveNormalized
      }
    });
    contributionByDate.set(r.date, list);
  });
  data.actualVsPredictedT12M.forEach(r => {
    const list = contributionByDate.get(r.date) ?? [];
    list.push({
      key: 'baseline',
      row: {
        Date: r.date,
        Source: 'Baseline / Organic',
        Channel: '',
        Platform: '',
        Tactic: '',
        'Brand/Nonbrand': '',
        Contribution: r.baselinePrediction
      }
    });
    contributionByDate.set(r.date, list);
  });

  const allRows: Record<string, string | number>[] = [];
  Array.from(contributionByDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([, items]) => {
      const ranked = [...items].sort(
        (a, b) => Number(b.row.Contribution) - Number(a.row.Contribution)
      );
      ranked.forEach((item, idx) => {
        allRows.push({ ...item.row, Rank: idx + 1 });
      });
    });

  return [{ name: 'Rank by Period', data: allRows }];
};

/**
 * Model Diagnostics — Actual vs Predicted. One row per period over the full
 * model history (the same window the chart shows, not just T12M). Columns are
 * named after the model's KPI. Baseline is included even though the chart
 * doesn't plot it, per the "most granular" export convention.
 */
export const shapeActualVsPredictedExport = (data: DashboardData): Sheet[] => {
  const kpiLabel = kpiLabelOf(data);
  const rows = [...data.actualVsPredicted]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(r => ({
      Date: r.date,
      [`Actual ${kpiLabel}`]: r.actualKpi,
      [`Predicted ${kpiLabel}`]: r.expectedKpi,
      Window: r.trainTest === 'test' ? 'Test' : 'Train'
    }));
  return [{ name: 'Actual vs Predicted', data: rows }];
};

/**
 * Drawer Trend — one sheet, per-date per-sub-channel rows. Parent total is
 * reconstructable by summing.
 */
export const shapeDrawerTrendExport = (drawerData: DrawerData): Sheet[] => {
  const rows: Record<string, string | number>[] = [];
  drawerData.subChannelTimeSeries.forEach((tsPoints, subChannelName) => {
    const sc = drawerData.subChannels.find(s => s.name === subChannelName);
    tsPoints.forEach(point => {
      rows.push({
        Date: point.date,
        Channel: sc?.channel ?? '',
        Platform: sc?.platform ?? '',
        Tactic: sc?.tactic ?? '',
        'Brand/Nonbrand': sc?.brandNonbrand ?? '',
        Spend: point.spend,
        Contribution: point.contribution
      });
    });
  });
  rows.sort((a, b) => {
    const dateA = String(a.Date);
    const dateB = String(b.Date);
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return String(a.Channel).localeCompare(String(b.Channel));
  });
  return [{ name: 'Sub-channel Trend', data: rows }];
};

/**
 * Drawer Saturation — one sheet, per sub-channel curve points.
 */
export const shapeDrawerSaturationExport = (drawerData: DrawerData): Sheet[] => {
  const rows: Record<string, string | number | boolean>[] = [];
  drawerData.saturationData.forEach(sat => {
    sat.rows.forEach(r => {
      rows.push({
        Channel: r.channel,
        Platform: r.platform,
        Tactic: r.tactic,
        'Brand/Nonbrand': r.brandNonbrand,
        'Adstock Spend': r.adstockSpend,
        'Effective Adstock Spend': r.effectiveAdstockSpend,
        Response: r.response,
        'Effective Response': r.effectiveResponse,
        'Next-Dollar Return': r.returnNextDollar,
        'Effective Next-Dollar Return': r.effectiveReturnNextDollar,
        'CI Lo (Response)': r.ciLoResponse,
        'CI Hi (Response)': r.ciHiResponse,
        'Is Mean Spend Point': r.isMean
      });
    });
  });
  return [{ name: 'Saturation Curves', data: rows }];
};

/**
 * Drawer Decay — one sheet, per sub-channel lag points.
 */
export const shapeDrawerDecayExport = (drawerData: DrawerData): Sheet[] => {
  const rows: Record<string, string | number>[] = [];
  drawerData.decayData.forEach(dec => {
    dec.rows.forEach(r => {
      rows.push({
        Channel: dec.channel,
        Platform: dec.platform,
        Tactic: '',
        'Brand/Nonbrand': '',
        'Period (weeks lag)': r.period,
        'Spend Carryover': r.spendCarryover,
        'CI Lo': r.ciLoCarryover,
        'CI Hi': r.ciHiCarryover,
        'Max Lag': r.maxLag
      });
    });
  });
  return [{ name: 'Decay Curves', data: rows }];
};

/**
 * Drawer Interchannel — two sheets. Reallocations from the 2-stage decomp
 * plus a demand-type summary.
 */
export const shapeDrawerInterchannelExport = (
  drawerData: DrawerData,
  fullData: DashboardData
): Sheet[] => {
  const featureLookup = new Map(fullData.channelHierarchy.channels.map(c => [c.name, c]));

  // Collect twoStageDecompRows that touch any sub-channel of this drawer's parent.
  const subChannelNames = new Set(drawerData.subChannels.map(sc => sc.name));
  const reallocations: Record<string, string | number>[] = [];
  fullData.twoStageDecompRows.forEach(r => {
    if (!subChannelNames.has(r.primaryName) && !subChannelNames.has(r.secondaryName)) return;
    const primary = featureLookup.get(r.primaryName);
    const secondary = featureLookup.get(r.secondaryName);
    reallocations.push({
      'Primary Channel': primary?.channel ?? '',
      'Primary Platform': primary?.platform ?? '',
      'Primary Tactic': primary?.tactic ?? '',
      'Primary Brand/Nonbrand': primary?.brandNonbrand ?? '',
      'Primary Media Type': r.primaryMediaType,
      'Secondary Channel': secondary?.channel ?? '',
      'Secondary Platform': secondary?.platform ?? '',
      'Secondary Tactic': secondary?.tactic ?? '',
      'Secondary Brand/Nonbrand': secondary?.brandNonbrand ?? '',
      'Secondary Media Type': r.secondaryMediaType,
      Direction: r.categoryType === 'added' ? 'Added' : 'Reduced',
      [r.spendOrEffect === 'spend' ? 'Spend Reallocated' : 'Effect Reallocated']: r.value
    });
  });

  const { contributionsByType } = drawerData;
  const demandSummary = [
    {
      'Demand Type': 'Demand Generation',
      'Direct Contribution': contributionsByType.demandGen.direct,
      'Actualized Contribution': contributionsByType.demandGen.actualized,
      Δ: contributionsByType.demandGen.actualized - contributionsByType.demandGen.direct
    },
    {
      'Demand Type': 'Demand Capture',
      'Direct Contribution': contributionsByType.demandCap.direct,
      'Actualized Contribution': contributionsByType.demandCap.actualized,
      Δ: contributionsByType.demandCap.actualized - contributionsByType.demandCap.direct
    }
  ];

  return [
    { name: 'Reallocations', data: reallocations },
    { name: 'Demand Type Summary', data: demandSummary }
  ];
};

interface MarketExportRow extends EfficiencyColumn {
  Market: string;
  'DMA Code': string;
  Spend: number;
  Contribution: number;
  'Spend Share %': number | '';
  'Contribution Share %': number | '';
}

/**
 * Market Performance's table, one row per market in the order the summary gives (named
 * markets by spend, then the pooled market), then the total. Shares are percents, as in the
 * channel export. A revenue model gets a ROAS column and any other a CPA column, as the table
 * does.
 */
export const shapeMarketTableExport = (
  markets: MarketSummary[],
  total: MarketTotals,
  isRevenue: boolean,
  dmaNames?: Record<string, string>
): MarketExportRow[] => [
  ...markets.map((m): MarketExportRow => ({
    Market: marketDisplayName(m.geo, dmaNames),
    'DMA Code': m.isPooled ? '' : marketDmaCode(m.geo),
    Spend: m.spend,
    Contribution: m.contribution,
    'Spend Share %': m.spendShare * 100,
    'Contribution Share %': m.contributionShare * 100,
    ...efficiencyColumn(m.spend, m.contribution, isRevenue)
  })),
  {
    Market: 'Total',
    'DMA Code': '',
    Spend: total.spend,
    Contribution: total.contribution,
    'Spend Share %': '',
    'Contribution Share %': '',
    ...efficiencyColumn(total.spend, total.contribution, isRevenue)
  }
];
